import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../firebase';
import { API_ENDPOINTS, backendConfig } from '../config/backendConfig';
import { getReactionArtworkId } from './reactionArtworkId';
import { enrichMuseumShare } from './museumShareAccess';

function normalizeMuseumShare(doc, source) {
  const data = doc.data();
  const item = { id: doc.id, ...data, source };
  const reactionArtworkId = getReactionArtworkId({
    id: doc.id,
    artworkId: data.artworkId || data.originalArtworkId || null,
  });
  const title =
    data.title ||
    data.artworkTitle ||
    data.styleName ||
    data.book ||
    'Shared artwork';
  const imageUrl =
    data.thumbnailUrl ||
    data.imageUrl ||
    data.artworkUrl ||
    data.transformedImageUrl ||
    data.artworkImageUrl ||
    '';

  return {
    id: doc.id,
    source,
    artworkId: data.artworkId || data.originalArtworkId || null,
    reactionArtworkId,
    title: String(title).trim() || 'Shared artwork',
    imageUrl,
    createdAt: data.createdAt,
  };
}

export async function fetchKudosCount(artworkId) {
  if (!artworkId) return 0;
  try {
    const resp = await fetch(
      `${API_ENDPOINTS.getKudosCount}?artworkId=${encodeURIComponent(artworkId)}`
    );
    const data = await resp.json();
    return data?.success ? (data.totalReactions ?? 0) : 0;
  } catch {
    return 0;
  }
}

/** Public shares across Artist Salon co-learning collections. */
export async function fetchMuseumShareStats(uid) {
  if (!uid) {
    return { totalShared: 0, bySource: {}, items: [] };
  }

  const [artHistorySnap, bookSnap, waysSnap, submissionsSnap] = await Promise.all([
    getDocs(
      query(
        collection(db, 'artHistorySharedArtworks'),
        where('uid', '==', uid),
        where('visibility', '==', 'public')
      )
    ),
    getDocs(
      query(
        collection(db, 'bookArtworks'),
        where('uid', '==', uid),
        where('visibility', '==', 'public')
      )
    ),
    getDocs(
      query(
        collection(db, 'tutorialArtworks'),
        where('userId', '==', uid),
        where('visibility', '==', 'public')
      )
    ),
    getDocs(
      query(
        collection(db, 'submissions'),
        where('uid', '==', uid),
        where('isPublic', '==', true)
      )
    ),
  ]);

  const bookItems = bookSnap.docs
    .map((d) => normalizeMuseumShare(d, 'book'))
    .filter((item) => {
      const uid = item.uid;
      return uid !== 'system-book-seed' && !item.isSystemSeed;
    });

  const items = [
    ...artHistorySnap.docs.map((d) => normalizeMuseumShare(d, 'art-history')),
    ...bookItems,
    ...waysSnap.docs.map((d) => normalizeMuseumShare(d, 'ways-of-seeing')),
    ...submissionsSnap.docs.map((d) => normalizeMuseumShare(d, 'activity')),
  ];

  const bySource = items.reduce((acc, item) => {
    acc[item.source] = (acc[item.source] || 0) + 1;
    return acc;
  }, {});

  return {
    totalShared: items.length,
    bySource,
    items,
  };
}

export async function fetchTopReactedMuseumArtworks(uid, limit = 8) {
  const { items } = await fetchMuseumShareStats(uid);
  if (!items.length) return [];

  const withCounts = await Promise.all(
    items.map(async (item) => {
      const totalReactions = await fetchKudosCount(item.reactionArtworkId);
      return { ...item, totalReactions };
    })
  );

  return withCounts
    .filter((item) => item.totalReactions > 0)
    .sort((a, b) => b.totalReactions - a.totalReactions)
    .slice(0, limit);
}

const MUSEUM_SOURCE_LABELS = {
  'art-history': 'Art History',
  book: 'Book Gallery',
  'ways-of-seeing': 'Ways of Seeing',
  activity: 'Daily Activities',
};

/** Public artist profile — museum shares list (Admin API, works without login). */
export async function fetchPublicMuseumInsights(uid) {
  if (!uid) {
    return { totalContributions: 0, shares: [] };
  }

  try {
    const response = await fetch(
      `${backendConfig.baseUrl}/api/consent/public-museum-insights/${encodeURIComponent(uid)}`
    );
    if (response.ok) {
      const data = await response.json();
      const shares = (Array.isArray(data.shares) ? data.shares : [])
        .map((s) => enrichMuseumShare(s))
        .filter(Boolean);
      return {
        totalContributions: data.totalContributions ?? 0,
        shares,
      };
    }
  } catch (err) {
    console.warn('[museumCoLearningStats] public insights API failed:', err);
  }

  const { items } = await fetchMuseumShareStats(uid);
  return {
    totalContributions: items.length,
    shares: items.map((item) => ({
      id: item.id,
      source: item.source,
      title: item.title,
      coverUrl: item.imageUrl,
      sourceLabel: MUSEUM_SOURCE_LABELS[item.source] || item.source,
      nav: {
        tab:
          item.source === 'book'
            ? 'books'
            : item.source === 'ways-of-seeing'
              ? 'ways'
              : item.source === 'art-history'
                ? 'art-history'
                : 'activities',
        submissionId: item.id,
      },
    })),
  };
}

export async function fetchReactionCountsForArtworks(artworks) {
  if (!Array.isArray(artworks) || artworks.length === 0) return {};

  const pairs = await Promise.all(
    artworks.map(async (art) => {
      const count = await fetchKudosCount(art?.id);
      return [art.id, count];
    })
  );

  return Object.fromEntries(pairs);
}
